import { useEffect, useState } from 'react';
import { FileSpreadsheet, AlertCircle, CheckCircle2, Send, Plus } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { taxFilingAdminApi, ApiError } from '@solara/api-client';
import type { TaxFilingDto } from '@solara/api-client';

export default function TaxReviewQueuePage() {
  const { t } = useTranslation('p1');
  const [filings, setFilings] = useState<TaxFilingDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [adjustment, setAdjustment] = useState<Record<string, { delta: string; reason: string }>>({});

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      setFilings(await taxFilingAdminApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function approveAdvisor(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await taxFilingAdminApi.taxAdvisorApprove(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('taxFiling.approveFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function submit(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await taxFilingAdminApi.submit(id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('taxFiling.submitFailed'));
    } finally {
      setBusyId(null);
    }
  }

  async function addAdjustment(id: string) {
    const entry = adjustment[id];
    if (!entry?.reason || entry.reason.length < 5) {
      setError(t('taxFiling.adjustmentReasonRequired'));
      return;
    }
    const delta = Math.round(Number(entry.delta) * 100);
    if (!Number.isFinite(delta)) {
      setError(t('taxFiling.adjustmentAmountInvalid'));
      return;
    }
    setBusyId(id);
    setError(null);
    try {
      await taxFilingAdminApi.addAdjustment(id, delta, entry.reason);
      setAdjustment((a) => ({ ...a, [id]: { delta: '', reason: '' } }));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('taxFiling.adjustmentFailed'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('taxFiling.reviewTitle')}</h1>
        <p className="text-fg-muted text-sm">{t('taxFiling.reviewSubtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      {filings.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-muted">{t('taxFiling.noQueueItems')}</p>
      ) : (
        <ul className="space-y-4">
          {filings.map((f) => (
            <Card key={f.id} className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4" />
                    {f.filingType} · {f.periodStart.slice(0, 10)} → {f.periodEnd.slice(0, 10)}
                  </span>
                  <Badge tone="neutral">
                    {t(`taxFiling.statuses.${f.status}`, { defaultValue: f.status })}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardBody className="space-y-3 text-sm">
                <p>
                  {t('taxFiling.tenantShort', { id: f.tenantId.slice(0, 8) })} ·{' '}
                  {t('taxFiling.due', { date: new Date(f.dueDate).toLocaleDateString() })} ·{' '}
                  {t('taxFiling.netPayable', {
                    amount: (f.netPayableMinor / 100).toLocaleString(),
                  })}
                </p>

                {f.status === 'DRAFT' || f.status === 'IN_REVIEW' ? (
                  <>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={adjustment[f.id]?.delta ?? ''}
                        onChange={(e) =>
                          setAdjustment((a) => ({
                            ...a,
                            [f.id]: { ...a[f.id], delta: e.target.value, reason: a[f.id]?.reason ?? '' },
                          }))
                        }
                        placeholder={t('taxFiling.adjustmentPlaceholder')}
                        className="w-32"
                      />
                      <Input
                        value={adjustment[f.id]?.reason ?? ''}
                        onChange={(e) =>
                          setAdjustment((a) => ({
                            ...a,
                            [f.id]: { ...a[f.id], delta: a[f.id]?.delta ?? '', reason: e.target.value },
                          }))
                        }
                        placeholder={t('taxFiling.adjustmentReasonPlaceholder')}
                      />
                      <Button variant="ghost" onClick={() => void addAdjustment(f.id)} disabled={busyId === f.id}>
                        <Plus className="mr-1 h-3 w-3" />
                        {t('actions.add')}
                      </Button>
                    </div>
                    <Button onClick={() => void approveAdvisor(f.id)} disabled={busyId === f.id}>
                      <CheckCircle2 className="mr-1 h-3 w-3" />
                      {t('taxFiling.approveSendClient')}
                    </Button>
                  </>
                ) : f.status === 'APPROVED_BY_TAX_ADVISOR' ? (
                  <Button onClick={() => void submit(f.id)} disabled={busyId === f.id}>
                    <Send className="mr-1 h-3 w-3" />
                    {t('taxFiling.submitToEmaraTax')}
                  </Button>
                ) : (
                  <p className="text-xs text-fg-muted">{t('taxFiling.noActions')}</p>
                )}
              </CardBody>
            </Card>
          ))}
        </ul>
      )}
    </div>
  );
}
